
import { getJobReSync, getShippingPDF } from '@/api';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import { User } from '@/models/main';
import { JobDetailedData } from '@/models/jobDetailed';
import { Component, Prop, Vue } from 'vue-property-decorator';
import IconWithTooltip from '@/components/UI/iconWithTooltip/IconWithTooltip.vue';
import { flattenObject } from '@/utils/InputProcessingUtilities';
import { notificationErrorText } from '@/utils/PortalConstants';
import { mapState } from 'vuex';
import SplitToBaseDialog from '@/components/jobDetailed/StopCompleteDialog/SplitToBaseDialog.vue';
import * as routeNames from '@/router/routeNames';

import DateTimezoneToggle from '@/components/UI/dateTimezoneToggle/DateTimezoneToggle.vue';
import JobCompliance from '@/views/JobBooking/JobCompliance.vue';
import OpenSchedulerBoardFromJobLink from '@/components/btns/OpenSchedulerBoardFromJobLink.vue';
import { cloneJob } from '@/helpers/booking';
import { fetchCanScheduleChange } from '@/helpers/fetchers';
import { getBaseName } from '@/utils/Common';

@Component({
  components: {
    BackButton,
    DateTimezoneToggle,
    JobCompliance,
    IconWithTooltip,
    OpenSchedulerBoardFromJobLink,
    SplitToBaseDialog,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class Header extends Vue {
  @Prop({ type: Object, required: true }) public readonly job!: JobDetailedData;

  public requestForChangeMessage = '';
  public isLoading = false;
  public loadingPDF = false;
  public isZammadLoading = false;
  public drawer: null | boolean = null;
  private user!: User;

  public splitToBaseDialogIsOpen = false;

  public routeNames = routeNames;
  public getBaseName = getBaseName;

  // Compliance modal
  public showModal = false;
  public currentJobIdx = -1;

  get jobStatus() {
    if (this.job.is_futile) {
      return 'Futile';
    } else {
      return this.job.status;
    }
  }

  public handleCloneJobClick(): void {
    cloneJob(this.job, this.$router);
  }

  public updateTable(date: string): void {
    this.job.rockhopper_date = date;
    this.$emit('fetchData', true, date);
  }

  public currentJobChanged(idx: number): void {
    this.currentJobIdx = idx;
  }

  public closeModal(): void {
    this.showModal = false;
    this.currentJobIdx = -1;
  }

  public openInEditModal(): void {
    this.currentJobIdx = 0;
    this.showModal = true;
  }
  // / Compliance modal

  public calcDelay(): string | void {
    if (this.job.delay[1] !== 0) {
      const numHours = Math.floor(this.job.delay[0] / 60 / 60);
      return `${numHours} ${numHours > 1 ? 'hrs' : 'hr'}`;
    }
  }

  public openRequestForChangeDialog() {
    const el = document.querySelector('[attr-scroll-id="newRequest"]');
    if (el) {
      const elDistanceToTop = window.pageYOffset + el.getBoundingClientRect().top;
      window.scrollTo({ top: elDistanceToTop, behavior: 'smooth' });
      // Close the panel
      this.drawer = false;
      // Open modal immediately if here is no space to scroll
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
        this.$emit('newTicketClicked');
      } else {
        // Or scroll to `zammad` section
        // Add listener to open the model when scroll finished
        if (!this.job.is_rfc) {
          window.onscroll = () => {
            const currentScrollOffset = window.pageYOffset || document.documentElement.scrollTop;
            const scrollStopPoint = document.documentElement.scrollHeight - window.screen.availHeight;
            if (currentScrollOffset >= scrollStopPoint - 20) {
              this.$emit('newTicketClicked');
              // Remove listener after scroll
              window.onscroll = null;
            }
          };
        }
      }
    }
  }

  /**
   * Sends request to API to change "can_schedule" for the job
   *
   * @param value - new value for "can_schedule"
   */
  public async handleCanScheduleChange(value: boolean): Promise<void> {
    this.isLoading = true;
    await fetchCanScheduleChange(value, this.job.rockhopper_id, this.job.rockhopper_date).catch(() => {
      // If request was not successful, revert toggle
      this.job.stops[0].can_schedule = !value;
    });
    this.isLoading = false;
  }

  public doReSync() {
    this.isLoading = true;
    getJobReSync(this.job.rockhopper_id, this.job.rockhopper_date)
      .then(() => {
        this.$emit('fetchData');
        this.$notify({
          group: 'network',
          title: 'Success',
          text: 'Job re-synced from the RH',
          type: 'success',
          duration: 3000,
        });
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error sending the request',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  public printPdf(): void {
    window.print();
  }

  public downloadCsv(): void {
    try {
      const flattenJob = flattenObject(this.job);
      const headers: string[] = [];
      Object.keys(flattenJob).map(header => {
        headers.push(`"${header}"`);
      });
      const data: string[] = [];
      Object.values(flattenJob).map(item => {
        data.push(`"${item}"`);
      });
      const csv = `${headers.toString()}
${data.toString()}`;
      const blob = new Blob([csv], { type: 'csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${this.job.rockhopper_id}-${this.job.rockhopper_date}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      this.$notify({
        group: 'network',
        title: 'Error downloading the CSV',
        text: notificationErrorText,
        type: 'warn',
      });
    }
  }

  public splitToBaseDialogToggle(): void {
    this.splitToBaseDialogIsOpen = !this.splitToBaseDialogIsOpen;
  }

  public downloadShippingPdf(job_id, job_date): void {
    this.loadingPDF = true;
    getShippingPDF(job_id, job_date)
      .then(res => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(res);
        link.download = `${job_id}-${job_date}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
        link.remove();
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error downloading the pdf',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.loadingPDF = false;
      });
  }
}
